import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DmcQuotesService } from 'src/app/business-service/dmc-quotes/dmc-quotes.service';
import { MEAL_PLANS } from 'src/app/modules/shared/constants/api-constants';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { InputValidationsService } from 'src/app/modules/shared/services/validations/input-validations.service';

@Component({
  selector: 'app-dmc-quote-hotel-pricing',
  templateUrl: './dmc-quote-hotel-pricing.component.html',
  styleUrls: ['./dmc-quote-hotel-pricing.component.scss'],
})
export class DmcQuoteHotelPricingComponent implements OnInit {

  @Input() leadId: any;
  @Input() itineraryId: any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter();

  @Output() updateQuote: EventEmitter<void> = new EventEmitter();
  @Output() updateInventoryItinerary: EventEmitter<void> = new EventEmitter();

  @Input() itineraryDetails: any;
  @Input() storedFormData: any;
  @Input() editInventory: boolean = false;
  @Input() editQuote: boolean = false;

  MEAL_PLAN = MEAL_PLANS

  hotelPricingForm!: FormGroup;
  hotelCategoryLov: any = [];
  meals: any = [];

  constructor(
    private _globalService: GlobalService,
    private _fb: FormBuilder,
    private _responseNotificationService: ResponseNotificationService,
    private _responseService: ResponseNotificationService,
    private _lovDataService: LovDataService,
    private _validationService: InputValidationsService,
    private _dmcQuoteService: DmcQuotesService
  ) { }

  ngOnInit() {
    this.hotelPricingForm = this._fb.group({
      itineraryId: [this.itineraryId],
      id: [null],
      organizationId: [null],
      section: [null],
      category: [null],
      ownership: [''],
      currency: ['USD', [Validators.required]],
      packages: this._fb.array([this.createPackageFormgroup()]),
      notes: ['']
    });

    this.hotelPricingForm.valueChanges.subscribe(() => {
      this.hotelPricingForm.markAsTouched();
      this.hotelPricingForm.markAsDirty();
    })

    this._lovDataService.getLovData("HotelType").subscribe((data) => {
      if (data) {
        this.hotelCategoryLov = data
      }
    });
    this._lovDataService.getLovData("Meals").subscribe((data) => {
      this.meals = data;
    });

    if (this.storedFormData) {
      this.patchHotelPricingData(this.storedFormData);
      if (this.storedFormData.dataInvalid) {
        this.hotelPricingForm.markAllAsTouched();
        this.hotelPricingForm.markAsDirty();
      }
    }

  }

  // Reactive forms funtions

  createPackageFormgroup(): FormGroup {
    let formGroup = this._fb.group({
      hotelCategory: ['', [Validators.required]],
      locations: this._fb.array(this.createDestinationsFormArray()),
      occupancyPricings: this._fb.array([this.createPaxFormgroup()])
    })
    return formGroup;
  }

  createLocationFormGroup(): FormGroup {
    let formGroup = this._fb.group({
      locationName: ['', [Validators.required]],
      nights: [null, [Validators.required]],
      mealPlan: [[]],
      hotelName: ['', [Validators.required]]
    });
    return formGroup;
  }

  createPaxFormgroup(): FormGroup {
    let formGroup = this._fb.group({
      paxRange: this._fb.group({
        minRange: [null, [Validators.required, this._validationService.valueNotZeroValidator()]],
        maxRange: [null, [Validators.required, this._validationService.valueNotZeroValidator()]],
      }, { validators: this._validationService.minMaxRangeValidator() }),
      landPax: [ null, [Validators.required, this._validationService.valueNotZeroValidator()]],
      occupancy: this._fb.group({
        sgl: [null, []],
        dbl: [null, []],
        tpl: [null, []]
      }, { validators: this.atLeastOneOccupancyRequired() }),
      childWithBed: [null, []],
      extraChildWithoutBed: [null, []]
    });
    return formGroup;
  }

  atLeastOneOccupancyRequired(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control || !(control instanceof FormGroup)) {
        return null;
      }

      const sgl = control.get('sgl')?.value;
      const dbl = control.get('dbl')?.value;
      const tpl = control.get('tpl')?.value;

      if (sgl || dbl || tpl) {
        return null; // At least one value exists, so no error
      }

      return { atLeastOneRequired: true }; // Error when all are null or empty
    };
  }

  getPackagesFormArray(): FormArray {
    return this.hotelPricingForm.get('packages') as FormArray;
  }

  getLocationsFormArray(packageIndex: number): FormArray {
    return this.getPackagesFormArray().at(packageIndex).get('locations') as FormArray;
  }

  getOccupancyPricingsFormArray(packageIndex: number): FormArray {
    return this.getPackagesFormArray().at(packageIndex).get('occupancyPricings') as FormArray;
  }

  addPackage() {
    const lastPackageIndex = this.getPackagesFormArray().length - 1;
    const lastPackageGroup = this.getPackagesFormArray().at(lastPackageIndex);
    const packageData = lastPackageGroup.getRawValue();
    let newPackageGroup = this.createPackageFormgroup();
    const { hotelCategory, occupancyPricings, locations } = packageData;

    let newOccupancyArray = newPackageGroup.get('occupancyPricings') as FormArray;
    newOccupancyArray.clear();

    occupancyPricings.forEach((occupancyItem: any, occupencyIndex: number) => {
      const occupancyFormGroup = this.createPaxFormgroup();
      occupancyFormGroup.patchValue({
        paxRange: occupancyItem.paxRange
      });
      if (occupencyIndex > 0) {
        occupancyFormGroup.get('paxRange.minRange')?.disable();
      }
      newOccupancyArray.push(occupancyFormGroup);
    });

    this.getPackagesFormArray().push(newPackageGroup);
  }

  addOccupancyPricing(packageIndex: number): void {
    const occupancyFormArray = this.getOccupancyPricingsFormArray(packageIndex);
    const lastOccupancyPricingIndex = occupancyFormArray.length - 1;
    const lastOccupancyData = occupancyFormArray.at(lastOccupancyPricingIndex)?.getRawValue();
    const newOccupancyPricing = this.createPaxFormgroup();
    if (lastOccupancyData?.paxRange?.maxRange != null) {
      newOccupancyPricing.get('paxRange')?.patchValue({
        minRange: +lastOccupancyData.paxRange?.maxRange + 1,
      });
      newOccupancyPricing.get('paxRange.minRange')?.disable();
    }
    occupancyFormArray.push(newOccupancyPricing);
  }


  removePackage(packageIndex: number) {
    this.getPackagesFormArray().removeAt(packageIndex);
  }

  removeOccupancyPricing(packageIndex: number, paxIndex: number) {
    this.getOccupancyPricingsFormArray(packageIndex).removeAt(paxIndex);
  }

  clearAllOccupancyPricing(packageIndex: number) {
    this.getOccupancyPricingsFormArray(packageIndex).clear();
    this.addOccupancyPricing(packageIndex);
  }

  // Patch form data funtions

  patchHotelPricingData(data: any) {
    const { packages, ...remainingData } = data;
    this.hotelPricingForm.patchValue(remainingData);
    this.patchPackageDetails(packages);
    this.hotelPricingForm.markAsPristine();
  }

  patchPackageDetails(packages: any) {
    const packagesFormArray = this.getPackagesFormArray();

    // Clear existing packages and repopulate
    packagesFormArray.clear();

    packages.forEach((packageItem: any, index: number) => {
      const { hotelCategory, occupancyPricings, locations } = packageItem;

      // Create new package form group
      const packageFormGroup = this.createPackageFormgroup();
      packageFormGroup.patchValue({ hotelCategory });

      // Patch locations
      const locationsFormArray = packageFormGroup.get('locations') as FormArray;
      locationsFormArray.controls.forEach(location => {
        let locationName = location.get('locationName')?.value;
        let storedLocationData = locations?.find((item: { locationName: any; }) => item.locationName == locationName);
        if (storedLocationData) {
          location.patchValue({
            hotelName: storedLocationData.hotelName,
            mealPlan: storedLocationData.mealPlan
          });
        }
      });


      // Add package to form array
      packagesFormArray.push(packageFormGroup);

      // Patch occupancy data separately
      this.patchOccupancyData(occupancyPricings, index);
    });
  }

  patchOccupancyData(occupancyData: any, packageIndex: number) {
    const occupancyFormArray = this.getOccupancyPricingsFormArray(packageIndex);

    // Clear existing occupancy data
    occupancyFormArray.clear();

    occupancyData.forEach((occupancyItem: any, occupencyIndex: number) => {
      const occupancyFormGroup = this.createPaxFormgroup();
      occupancyFormGroup.patchValue(occupancyItem);
      if (occupencyIndex > 0) {
        occupancyFormGroup.get('paxRange.minRange')?.disable();
      }
      occupancyFormArray.push(occupancyFormGroup);
    });
  }

  // Helper funtions
  createDestinationsFormArray(): any {
    if (this.itineraryDetails) {
      let data: FormGroup<any>[] = [];
      this.itineraryDetails.destination?.forEach((destination: any) => {
        let formGroup = this.createLocationFormGroup();
        formGroup.patchValue({
          locationName: destination.location,
          nights: destination.days,
          mealPlan: []
        })
        data.push(formGroup);
      });
      return data;
    } else {
      this._responseNotificationService.showMessage("Package Details is not entered", true)
      return [];
    }
  }

  maxRangeChanged(event: any, packageIndex: number, occupancyIndex: number): void {
    const value = +(event.target.value);
    const formArray = this.getOccupancyPricingsFormArray(packageIndex);
    const nextOccupancy = formArray.at(occupancyIndex + 1)
    if (nextOccupancy) {
      nextOccupancy.get('paxRange.minRange')?.patchValue(value + 1);
    }
  }

  checkInvalidControl(group: any, formControlName: string): boolean {
    const control = group.get(formControlName)
    return control?.invalid && (control?.touched || control?.dirty);
  }

  async onSubmit(): Promise<boolean> {
    if (this.hotelPricingForm.valid) {
      let formData = await this.serializeFormData(this.hotelPricingForm.getRawValue());
      return this.handleLeadSpecificHotelPricing(formData);
    } else {
      this.triggerInvalidForm();
      return false;
    }
  }

  triggerInvalidForm(){
    this.hotelPricingForm.markAllAsTouched();
    this._globalService.scrollToFirstInvalidControl();
    this._globalService.customAlert("Fill all required fields to save", "Invalid Form");
    return false;
  }

  private async handleLeadSpecificHotelPricing(formData: any): Promise<boolean> {
    if (this.editQuote) {
      this.finalizeFormSubmission(formData);
      this.updateQuote.emit();
      return true;
    }

    try {
      const data = await this._dmcQuoteService.addHotelPricing(formData).toPromise();
      this._responseNotificationService.showMessage("Hotel Pricing Added!");
      this.finalizeFormSubmission(data);
      return true;
    } catch (error) {
      console.error("Error adding hotel pricing to lead", error);
      return false;
    }
  }

   serializeFormData(formData: any) {
    return formData;
  }

  private finalizeFormSubmission(data: any): void {
    this.hotelPricingForm.markAsPristine();
    data.dataSaved = true;
    this.storeFormData.emit(data);
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext() {
    if (await this.onSubmit()) {
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.hotelPricingForm.dirty || this.hotelPricingForm.invalid) && this.hotelPricingForm.touched;
  }

}
